import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { Button, Loading } from 'savings-js';
import './ProgramForm.css';

// Types
interface Partner {
  id: number;
  name: string;
}

interface Trigger {
  id: number;
  name: string;
}

interface Precondition {
  id: number;
  name: string;
}

interface ProgramFormData {
  name: string;
  valuePaid: number;
  dateStart: string;
  dateEnd?: string;
  partnerId: number;
  triggerId: number;
  preconditionId: number;
}

const ProgramForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [partners, setPartners] = useState<Partner[]>([]);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [preconditions, setPreconditions] = useState<Precondition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { control, handleSubmit, reset, formState: { errors } } = useForm<ProgramFormData>();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [partnersRes, triggersRes, preconditionsRes] = await Promise.all([
          axios.get('/api/partners'),
          axios.get('/api/triggers'),
          axios.get('/api/preconditions'),
        ]);
        
        setPartners(partnersRes.data);
        setTriggers(triggersRes.data);
        setPreconditions(preconditionsRes.data);
        
        // If in edit mode, fetch the program data
        if (isEditMode) {
          try {
            const programRes = await axios.get(`/api/programs/${id}`);
            const program = programRes.data;
            
            // Format dates for the form and map field names
            const formattedProgram = {
              name: program.name,
              valuePaid: program.value_paid || program.valuePaid,
              dateStart: program.date_start ? program.date_start.split('T')[0] : program.dateStart ? program.dateStart.split('T')[0] : '',
              dateEnd: program.date_end ? program.date_end.split('T')[0] : program.dateEnd ? program.dateEnd.split('T')[0] : undefined,
              partnerId: program.partner_id || program.partnerId,
              triggerId: program.trigger_id || program.triggerId,
              preconditionId: program.precondition_id || program.preconditionId
            };
            
            reset(formattedProgram);
          } catch (err) {
            console.error('Error fetching program:', err);
            setError(`Program with ID ${id} not found. Please check the URL and try again.`);
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditMode, reset]);
  
  const onSubmit = async (data: ProgramFormData) => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      
      if (isEditMode) {
        await axios.put(`/api/programs/${id}`, data);
        setSuccessMessage('Program updated successfully!');
        
        // Wait a moment to show the success message before redirecting
        setTimeout(() => {
          navigate('/programs', { 
            state: { 
              success: true, 
              message: 'Program updated successfully!' 
            } 
          });
        }, 1500);
      } else {
        await axios.post('/api/programs', data);
        setSuccessMessage('Program created successfully!');
        
        // Wait a moment to show the success message before redirecting
        setTimeout(() => {
          navigate('/programs', { 
            state: { 
              success: true, 
              message: 'Program created successfully!' 
            } 
          });
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      
      // Extract error message from the response if available
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message;
      setError(`Failed to ${isEditMode ? 'update' : 'create'} program: ${errorMessage}`);
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-box">
          <h1 className="loading-title">Loading...</h1>
          <div className="loading-spinner-container">
            <Loading />
          </div>
        </div>
      </div>
    );
  }
  
  if (error && !submitting) {
    return (
      <div className="error-container">
        <div className="error-box">
          <h1 className="error-title">Error</h1>
          <p className="error-message">{error}</p>
          <Button 
            onClick={() => window.location.reload()}
            label="Retry"
            color="primary"
            className="mx-auto mt-4"
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className="program-form-container">
      <div className="header">
        <h1>{isEditMode ? 'Edit Program' : 'Create New Program'}</h1>
      </div>
      
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
      
      {error && submitting && (
        <div className="form-error-message">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="program-form">
        <div className="form-group">
          <label htmlFor="name">Program Name</label>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Program name is required' }}
            render={({ field }) => (
              <input
                id="name"
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                placeholder="Enter program name"
                {...field}
              />
            )}
          />
          {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="valuePaid">Value Paid ($)</label>
          <Controller
            name="valuePaid"
            control={control}
            rules={{ 
              required: 'Value is required',
              min: { value: 0.01, message: 'Value must be greater than 0' }
            }}
            render={({ field }) => (
              <input
                id="valuePaid"
                type="number"
                step="0.01"
                className={`form-control ${errors.valuePaid ? 'is-invalid' : ''}`}
                placeholder="Enter value paid"
                {...field}
              />
            )}
          />
          {errors.valuePaid && <div className="invalid-feedback">{errors.valuePaid.message}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="dateStart">Start Date</label>
          <Controller
            name="dateStart"
            control={control}
            rules={{ required: 'Start date is required' }}
            render={({ field }) => (
              <input
                id="dateStart"
                type="date"
                className={`form-control ${errors.dateStart ? 'is-invalid' : ''}`}
                {...field}
              />
            )}
          />
          {errors.dateStart && <div className="invalid-feedback">{errors.dateStart.message}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="dateEnd">End Date (Optional)</label>
          <Controller
            name="dateEnd"
            control={control}
            render={({ field }) => (
              <input
                id="dateEnd"
                type="date"
                className="form-control"
                {...field}
              />
            )}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="partnerId">Partner</label>
          <Controller
            name="partnerId"
            control={control}
            rules={{ required: 'Partner is required' }}
            render={({ field }) => (
              <select
                id="partnerId"
                className={`form-control ${errors.partnerId ? 'is-invalid' : ''}`}
                {...field}
              >
                <option value="">Select a partner</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.partnerId && <div className="invalid-feedback">{errors.partnerId.message}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="triggerId">Trigger</label>
          <Controller
            name="triggerId"
            control={control}
            rules={{ required: 'Trigger is required' }}
            render={({ field }) => (
              <select
                id="triggerId"
                className={`form-control ${errors.triggerId ? 'is-invalid' : ''}`}
                {...field}
              >
                <option value="">Select a trigger</option>
                {triggers.map(trigger => (
                  <option key={trigger.id} value={trigger.id}>
                    {trigger.name}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.triggerId && <div className="invalid-feedback">{errors.triggerId.message}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="preconditionId">Precondition</label>
          <Controller
            name="preconditionId"
            control={control}
            rules={{ required: 'Precondition is required' }}
            render={({ field }) => (
              <select
                id="preconditionId"
                className={`form-control ${errors.preconditionId ? 'is-invalid' : ''}`}
                {...field}
              >
                <option value="">Select a precondition</option>
                {preconditions.map(precondition => (
                  <option key={precondition.id} value={precondition.id}>
                    {precondition.name}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.preconditionId && <div className="invalid-feedback">{errors.preconditionId.message}</div>}
        </div>
        
        <div className="form-actions">
          <Button
            onClick={() => navigate('/programs')}
            label="Cancel"
            color="gray"
          />
          <Button
            onClick={handleSubmit(onSubmit)}
            label={submitting ? 'Saving...' : (isEditMode ? 'Update Program' : 'Create Program')}
            color="primary"
          />
        </div>
      </form>
    </div>
  );
};

export default ProgramForm; 